import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { releaseHistoryQuery, releaseProductsQuery } from "~/modules/release-history/queries";
import { ReleaseHistoryPage } from "~/modules/release-history/release-history-page";

export const Route = createFileRoute("/_dash/release-history")({
  validateSearch: z.object({
    product: z.string().optional(),
    version: z.string().optional(),
    q: z.string().optional(),
  }),
  component: ReleaseHistoryRoute,
});
function ReleaseHistoryRoute() {
  return (
    <PermissionGuard permission="release-history:view">
      <ReleaseHistoryData />
    </PermissionGuard>
  );
}
function ReleaseHistoryData() {
  const { version, q, product = "captions-ios" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, error, isLoading, isFetching, refetch } = releaseHistoryQuery.useQuery(
    { pathParams: { productId: product } },
    { staleTime: 30_000, refetchInterval: 60_000 }
  );
  const products = releaseProductsQuery.useQuery({ staleTime: 60_000 });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3 text-card-foreground">
        <div className="flex items-center gap-3 text-sm">
          <label htmlFor="release-history-product">Product</label>
          <Select
            value={product}
            onValueChange={(product) => void navigate({ search: { product } })}
          >
            <SelectTrigger id="release-history-product" className="max-w-full bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(products.data?.products || [{ id: product, name: product }]).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 text-sm">
          <span className="text-muted-foreground">
            {data
              ? `Last checked ${new Date(data.fetchedAt).toLocaleString()}`
              : "Loading release history"}
          </span>
          <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
            {isFetching ? "Checking…" : "Refresh"}
          </Button>
        </div>
      </div>
      {products.error && (
        <p role="alert" className="text-sm text-warning-foreground">
          Could not refresh the product list. The current product remains available.
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="text-danger-foreground bg-danger-muted rounded-lg border border-danger-border p-3 text-sm"
        >
          Unable to refresh release history. {data ? "Showing the last successful snapshot. " : ""}
          Try Refresh; if this persists, contact the release-history owner.
        </p>
      )}
      {isLoading && (
        <div role="status" aria-label="Loading releases" className="space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <span className="sr-only">Loading releases…</span>
        </div>
      )}
      {data && (
        <ReleaseHistoryPage
          key={product}
          data={data}
          version={version}
          query={q || ""}
          onSelect={(version) => void navigate({ search: { product, version } })}
          onSearch={(q) =>
            void navigate({ search: { product, version, q: q || undefined }, replace: true })
          }
        />
      )}
    </div>
  );
}
